import { Sorteio } from "./analiseEngine";

export type DezenaFrenteFundo = {
  valor: string;
  ocorrencias: number;
  percentual: number;
  ultimaData: string | null;
  atraso: number;
  turnos: string[];
  posicoes: number[];
};

export type MilharFrenteFundo = {
  milhar: string;
  frente: string;
  fundo: string;
  pontuacao: number;
  ocorrenciasJuntas: number;
  justificativa: string;
};

export type AnaliseFrenteFundo = {
  fonte: string;
  totalResultados: number;
  totalSorteios: number;
  frentes: DezenaFrenteFundo[];
  fundos: DezenaFrenteFundo[];
  milhares: MilharFrenteFundo[];
  pesos: {
    frequenciaFrente: number;
    frequenciaFundo: number;
    combinacaoCompleta: number;
    recenciaAtraso: number;
    distribuicaoPremios: number;
  };
};

type Acumulador = {
  ocorrencias: number;
  ultimoIndice: number;
  ultimaData: string;
  turnos: Set<string>;
  posicoes: Set<number>;
};

const normalizarMilhar = (valor: string) => String(valor).replace(/\D/g, "").slice(-4).padStart(4, "0");

const normalizar = (valor: number, maximo: number) => maximo > 0 ? (valor / maximo) * 100 : 0;

const criarRanking = (
  acumuladores: Map<string, Acumulador>,
  totalResultados: number,
): DezenaFrenteFundo[] => [...acumuladores.entries()]
  .map(([valor, dados]) => ({
    valor,
    ocorrencias: dados.ocorrencias,
    percentual: totalResultados > 0 ? (dados.ocorrencias / totalResultados) * 100 : 0,
    ultimaData: dados.ultimaData || null,
    atraso: Math.max(0, dados.ultimoIndice),
    turnos: [...dados.turnos].sort(),
    posicoes: [...dados.posicoes].sort((a, b) => a - b),
  }))
  .sort((a, b) => b.ocorrencias - a.ocorrencias || a.atraso - b.atraso || a.valor.localeCompare(b.valor))
  .slice(0, 4);

const selecionarComDiversidade = (candidatas: MilharFrenteFundo[]) => {
  const escolhidas: MilharFrenteFundo[] = [];
  const restantes = [...candidatas];
  while (escolhidas.length < 3 && restantes.length) {
    const melhorPontuacao = restantes[0].pontuacao;
    const faixaProxima = restantes.filter((item) => melhorPontuacao - item.pontuacao <= 5);
    const diversa = faixaProxima.find((item) =>
      escolhidas.every((escolhida) => escolhida.frente !== item.frente && escolhida.fundo !== item.fundo)
    );
    const escolhida = diversa || faixaProxima.find((item) =>
      escolhidas.every((anterior) => anterior.frente !== item.frente || anterior.fundo !== item.fundo)
    ) || restantes[0];
    escolhidas.push(escolhida);
    restantes.splice(restantes.indexOf(escolhida), 1);
  }
  return escolhidas;
};

export const analisarDezenasFrenteFundo = (sorteios: Sorteio[], fonte: string): AnaliseFrenteFundo => {
  const recorte = sorteios.filter((sorteio) => sorteio.fonte === fonte);
  const frentes = new Map<string, Acumulador>();
  const fundos = new Map<string, Acumulador>();
  const milhares = new Map<string, Acumulador>();
  let totalResultados = 0;

  const registrar = (mapa: Map<string, Acumulador>, valor: string, indice: number, data: string, turno: string, posicao: number) => {
    const atual = mapa.get(valor) || {
      ocorrencias: 0,
      ultimoIndice: indice,
      ultimaData: data,
      turnos: new Set<string>(),
      posicoes: new Set<number>(),
    };
    atual.ocorrencias += 1;
    if (indice < atual.ultimoIndice) {
      atual.ultimoIndice = indice;
      atual.ultimaData = data;
    }
    atual.turnos.add(turno);
    if (posicao >= 1 && posicao <= 5) atual.posicoes.add(posicao);
    mapa.set(valor, atual);
  };

  recorte.forEach((sorteio, indice) => {
    sorteio.rows.filter((row) => Number(row.posicao) >= 1 && Number(row.posicao) <= 5).forEach((row) => {
      const milhar = normalizarMilhar(row.milhar);
      const frente = milhar.slice(0, 2);
      const fundo = milhar.slice(2);
      totalResultados += 1;
      registrar(frentes, frente, indice, sorteio.data, sorteio.turno, Number(row.posicao));
      registrar(fundos, fundo, indice, sorteio.data, sorteio.turno, Number(row.posicao));
      registrar(milhares, milhar, indice, sorteio.data, sorteio.turno, Number(row.posicao));
    });
  });

  const rankingFrentes = criarRanking(frentes, totalResultados);
  const rankingFundos = criarRanking(fundos, totalResultados);
  const maxFrente = rankingFrentes[0]?.ocorrencias || 0;
  const maxFundo = rankingFundos[0]?.ocorrencias || 0;
  const maxMilhar = Math.max(0, ...[...milhares.values()].map((item) => item.ocorrencias));
  const candidatos = rankingFrentes.flatMap((frente) => rankingFundos.map((fundo) => {
    const milhar = `${frente.valor}${fundo.valor}`;
    const conjunto = milhares.get(milhar);
    const ocorrenciasJuntas = conjunto?.ocorrencias || 0;
    const recenciaFrente = Math.max(0, 100 - normalizar(frente.atraso, Math.max(recorte.length - 1, 1)));
    const recenciaFundo = Math.max(0, 100 - normalizar(fundo.atraso, Math.max(recorte.length - 1, 1)));
    const recenciaAtraso = (recenciaFrente + recenciaFundo) / 2;
    const distribuicao = normalizar(conjunto?.posicoes.size || 0, 5);
    const pontuacao =
      normalizar(frente.ocorrencias, maxFrente) * 0.30
      + normalizar(fundo.ocorrencias, maxFundo) * 0.30
      + normalizar(ocorrenciasJuntas, maxMilhar) * 0.20
      + recenciaAtraso * 0.10
      + distribuicao * 0.10;
    const justificativa = ocorrenciasJuntas > 0
      ? `A frente ${frente.valor} e o fundo ${fundo.valor} estão entre os mais frequentes e já formaram esta milhar ${ocorrenciasJuntas} vez(es) no recorte.`
      : `A frente ${frente.valor} e o fundo ${fundo.valor} estão entre os mais frequentes; esta união ganhou força pela combinação dos dois rankings.`;
    return {
      milhar,
      frente: frente.valor,
      fundo: fundo.valor,
      pontuacao: Number(pontuacao.toFixed(1)),
      ocorrenciasJuntas,
      justificativa,
    };
  })).sort((a, b) => b.pontuacao - a.pontuacao || b.ocorrenciasJuntas - a.ocorrenciasJuntas || a.milhar.localeCompare(b.milhar));

  return {
    fonte,
    totalResultados,
    totalSorteios: recorte.length,
    frentes: rankingFrentes,
    fundos: rankingFundos,
    milhares: selecionarComDiversidade(candidatos),
    pesos: {
      frequenciaFrente: 30,
      frequenciaFundo: 30,
      combinacaoCompleta: 20,
      recenciaAtraso: 10,
      distribuicaoPremios: 10,
    },
  };
};